import { useCallback, useEffect, useRef, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import useWindowSize from '@hooks/use-window-size';
import {
  CatalogDatalayerType,
  getCatalogTrackingData,
  getProductTrackingData,
} from '@helpers/utils/catalog/catalog-tracking-utils';
import { useRouter } from 'next/router';
import { RESULTS_PER_PAGE } from '@constants/catalog';

export const getImpressionTrackingIndex = ({
  index,
  currentPage,
  isWiderThanMd,
}: {
  index: number;
  currentPage: number;
  isWiderThanMd: boolean;
}) => index + 1 + (isWiderThanMd ? (currentPage - 1) * RESULTS_PER_PAGE : 0);

const ROW_COUNT_BREAKPOINTS = [
  { width: 1420, count: 5 },
  { width: 1024, count: 4 },
  { width: 640, count: 3 },
] as const;

const getRowCount = (windowWidth: number) => {
  for (const { width, count } of ROW_COUNT_BREAKPOINTS) {
    if (windowWidth >= width) {
      return count;
    }
  }
  return 2; // mobile fallback
};

const useProductImpressionsTracking = ({
  onRowVisibleCallback,
}: {
  onRowVisibleCallback: (input: CatalogProduct[]) => void;
}) => {
  const queryResult = useContextSelector(CatalogContext, (v) => v.queryResult);
  const { windowSize } = useWindowSize();
  const [productsLoaded, setProductsLoaded] = useState(false);
  const lastCatalogTrackingDataRef = useRef<Partial<CatalogDatalayerType> | null>(null);
  const { query } = useRouter();
  const alertId = query?.alertId;

  const rowCount = getRowCount(windowSize.width);
  const numberOfProductsPerEvent = rowCount === 5 ? 15 : 12; // each impression event should contain products from at least 3 rows
  const seenProductsDataRef = useRef([]);

  const { sendAnalyticEvent } = useAnalyticEvents('product');

  // This useEffect is used to send impression event for any remaining products when user leaves the page
  useEffect(() => {
    const sendLastProductImpressionsEvent = () => {
      if (seenProductsDataRef.current.length > 0) {
        sendAnalyticEvent('product_impression', {
          ...getCatalogTrackingData(),
          product_card_details: seenProductsDataRef.current,
        });
      }
    };
    // If users exits page without component unmounting (e.g. by clicking in link header)
    // we send a tracking event for all remaining products
    window.addEventListener('beforeunload', sendLastProductImpressionsEvent);

    return () => {
      window.removeEventListener('beforeunload', sendLastProductImpressionsEvent);
      // If users exits page and component unmounts ( i.e. via next routing, for example when clicking on product card )
      // we send a tracking event for all remaining products
      sendLastProductImpressionsEvent();
    };
    // sendAnalyticEvent and  shouldn't change, catalogTrackingDataRef is a ref, so we don't need them in the deps
  }, []);

  // this fn is used to send product impressions events on scroll and when products changes ( i.e. via pagination, filtering )
  const sendProductImpressionsEvent = useCallback(
    (catalogTrackingData: Partial<CatalogDatalayerType>) => {
      if (seenProductsDataRef.current.length === 0) {
        return;
      }
      const productData = seenProductsDataRef.current;
      // resetting the seen products
      seenProductsDataRef.current = [];
      const sendTrackingEvents = () =>
        sendAnalyticEvent('product_impression', {
          ...catalogTrackingData,
          product_card_details: productData,
        });
      // using requestIdleCallback to avoid blocking the main thread
      window.requestIdleCallback(sendTrackingEvents);
    },
    // sendAnalyticEvent and  shouldn't change, catalogTrackingDataRef is a ref, so we don't need them in the deps
    [],
  );

  // initial population of lastCatalogTrackingDataRef, used for sending remaining impressions when queryResult changes
  useEffect(() => {
    if (productsLoaded && !lastCatalogTrackingDataRef.current) {
      lastCatalogTrackingDataRef.current = getCatalogTrackingData();
    }
  }, [productsLoaded]);

  // this effects sends remaining impressions when queryResult changes - uses the lastCatalogTrackingDataRef instead of getCatalogTrackingData
  // to avoid using the "new" catalog state
  useEffect(() => {
    sendProductImpressionsEvent(lastCatalogTrackingDataRef.current ?? {});
  }, [queryResult]);

  // this fn is used to update the latestProductIndex onScroll, and send tracking event for every 12 or 15 rows
  const updateLatestProductIndex = useCallback(
    (index: number, productsOnRow: CatalogProduct[]) => {
      onRowVisibleCallback?.(productsOnRow);
      const newProductData = productsOnRow.map((product, i) => ({
        ...getProductTrackingData(product),
        product_position: String(index + i - 1), // subtract on since index is 0 based
        ...(alertId ? { product_feed_category: `saved_searches_${alertId}` } : {}),
      }));
      seenProductsDataRef.current = [...seenProductsDataRef.current, ...newProductData];
      if (!productsLoaded) {
        setProductsLoaded(true);
      }
      if (seenProductsDataRef.current.length === numberOfProductsPerEvent) {
        lastCatalogTrackingDataRef.current = getCatalogTrackingData();
        sendProductImpressionsEvent(lastCatalogTrackingDataRef.current);
      }
    },
    [rowCount, sendProductImpressionsEvent, onRowVisibleCallback],
  );

  return { updateLatestProductIndex, rowCount };
};

export default useProductImpressionsTracking;
